import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, LockKeyhole } from "lucide-react";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, ErrorState, Skeleton, PrimaryButton } from "@/components/BookingShell";
import { formatPrice, formatDuration } from "@/lib/format";
import { groupServices, hasEligibleMain } from "@/lib/serviceGroups";
import { TreatmentDetails, treatmentBlock } from "@/components/TreatmentDetails";
import { BookingSuggestions } from "@/components/BookingSuggestions";
import {OfferPrice} from '@/components/OfferPrice';

export function ServiceStep({ setFooter }) {
    const { category, service, setService, next, cart, setCart, goTo, setDate, setTime } = useBooking();
    const [services, setServices] = useState(null);
    const [rules, setRules] = useState({});
    const [expanded, setExpanded] = useState(null);
    const [details, setDetails] = useState(null);
    const [reviewing, setReviewing] = useState(false);
    const [multi, setMulti] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        setError(null);
        setServices(null);
        api.catalogue().then((d) => { if (active) {
            setRules(d.rules);
            setMulti(d.multi_checkout_enabled);
            if (!d.multi_checkout_enabled) {
                setCart([]);
                setService((previous) => previous?.categoryId === category?.id ? previous : null);
            }
            setServices(d.services.filter((s) => s.categoryId === category?.id && (d.multi_checkout_enabled || (d.rules[String(s.id)]?.type || "main") === "main")));
        } }).catch((e) => { if (active) setError(e); });
        return () => { active = false; };
    }, [category?.id, setCart, setService]);

    const selected = service;
    const continueBooking = useCallback(async () => {
        if (!multi) { next(); return; }
        if (cart.length === 1 && cart[0].quantity === 1 && (rules[String(cart[0].service.id)]?.type || "main") === "main") {
            setService(cart[0].service);
            next();
            return;
        }
        setBusy(true); setError(null);
        try {
            const quote = await api.cartQuote(cart.map((c) => ({service_id:c.service.id, quantity:c.quantity})));
            setService({ id: cart[0].service.id, name: quote.items.map((i) => `${i.name}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`).join(" + "), price:quote.price, duration:quote.duration, deposit:quote.amount_due, depositPayment:"fixed", cartQuote:quote });
            next();
        } catch (e) { setError(e); } finally { setBusy(false); }
    }, [multi, next, cart, setService, rules]);
    const select = (s) => {
        if (treatmentBlock(s, rules, cart)) return;
        if (!multi) { setService(s); setDate(null); setTime(null); return; }
        let items = cart.some((c) => c.service.id === s.id) ? cart.filter((c) => c.service.id !== s.id) : [...cart, {service:s, quantity:rules[String(s.id)]?.quantity_min || 1}];
        setDate(null); setTime(null);
        const ids = items.map((c) => c.service.id);
        items = items.filter((c) => !rules[String(c.service.id)]?.requires?.length || hasEligibleMain(rules[String(c.service.id)].requires, ids, rules));
        setCart(items);
    };

    useEffect(() => {
        setFooter(
            (multi ? cart.length > 0 : selected) ? (
                <PrimaryButton onClick={continueBooking} disabled={busy} testId="service-continue-button">
                    {busy ? "Checking treatments…" : "Continue"}
                </PrimaryButton>
            ) : null
        );
        return () => setFooter(null);
    }, [selected, multi, cart.length, continueBooking, busy, setFooter]);

    const groups = useMemo(() => groupServices(services || [], rules), [services, rules]);

    return (
        <div>
            <StepTitle
                eyebrow={category?.name || "Treatment"}
                title={<>Select your <em className="italic gold-text not-italic font-serif">treatment</em></>}
                subtitle="All treatments include a private consultation with Angela."
                testId="service-step-title"
            />

            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}
            {multi && cart.length > 0 && <div className="rounded-2xl border border-gold bg-gold/20 p-4 mb-4 text-white">
                <p className="font-semibold">Current selections</p>
                {cart.map(c => <div key={c.service.id} className="py-2 text-sm">
                    <div className="flex items-center justify-between gap-3"><span>{c.service.name}{rules[String(c.service.id)]?.option_label ? ` · ${rules[String(c.service.id)].option_label}` : ""} × {c.quantity}</span><button className="min-h-11 text-gold" onClick={() => select(c.service)}>Remove</button></div>
                    {rules[String(c.service.id)]?.quantity_enabled && <label className="flex items-center justify-between gap-3">Quantity<input aria-label={`Quantity for ${c.service.name}`} type="number" min={rules[String(c.service.id)].quantity_min} max={rules[String(c.service.id)].quantity_max} step="1" value={c.quantity} onChange={e => setCart(cart.map(item => item.service.id === c.service.id ? {...item,quantity:Number(e.target.value)} : item))} className="w-20 min-h-11 rounded border border-gold bg-navy px-3"/></label>}
                </div>)}
                <button className="min-h-11 text-gold underline" onClick={() => goTo("category")}>Add another treatment</button>
            </div>}

            <BookingSuggestions />
            {services?.some(s=>s.offer || s.offer_preview?.length) && <div className="rounded-2xl border border-gold/50 p-4 mb-5"><h2 className="font-serif text-xl text-gold">Current offers</h2><p className="text-sm mt-2">Look for the offer labels below. Your final price is confirmed after choosing your appointment date.</p></div>}

            {!services && !error && (
                <div className="space-y-3">
                    {[1, 2, 3].map((k) => (
                        <Skeleton key={k} className="h-24" />
                    ))}
                </div>
            )}

            {services && groups.length === 0 && (
                <div className="rounded-2xl glass p-8 text-center" data-testid="service-empty-state">
                    <div className="font-serif text-2xl">No treatments in this category</div>
                </div>
            )}

            {reviewing && cart.length > 0 && <button type="button" className="min-h-11 text-gold underline mb-4" onClick={() => setReviewing(false)}>Browse this category</button>}
            {(!reviewing || !cart.length) && groups.length > 0 && <div data-testid="service-list" className="space-y-8">
                {["main", "addon"].map(type => {
                    const sections = groups.map(g => ({...g, options:g.options.filter(s => (rules[String(s.id)]?.type || "main") === type)})).filter(g => g.options.length);
                    if (!sections.length) return null;
                    return <section key={type} aria-label={type === "main" ? "Main area treatments" : "Treatment Addons"}>
                        <h2 className="font-serif text-2xl text-gold mb-2">{type === "main" ? "Main area treatments" : "Treatment Addons"}</h2>
                        <p className="text-sm text-white/60 mb-4">{type === "main" ? "View a treatment and confirm your choice." : "Select a main area treatment first. Tap an add-on to view its details."}</p>
                        <ul className="space-y-3">{sections.map(g => <React.Fragment key={g.parent.id}>
                            {g.hasVariants && <li><button type="button" aria-expanded={expanded === g.parent.id} onClick={() => setExpanded(expanded === g.parent.id ? null : g.parent.id)} className="w-full text-left rounded-2xl border border-gold/50 p-5 text-white">
                                <span className="font-serif text-xl">{g.parent.name}</span>
                                <span className="block text-sm text-white/70 mt-2">From {formatPrice(Math.min(...g.options.map(s => s.price)))} · {g.options.length} options</span>
                                <span className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-4 py-2 mt-3 font-semibold">{expanded === g.parent.id ? "Hide options" : "View treatment options"}<ChevronDown size={20} className={expanded === g.parent.id ? "rotate-180" : ""}/></span>
                            </button></li>}
                            {(!g.hasVariants || expanded === g.parent.id ? g.options : []).map(s => {
                                const active = multi ? cart.some(c => c.service.id === s.id) : selected?.id === s.id;
                                const r = rules[String(s.id)] || {};
                                const blocked = treatmentBlock(s, rules, cart);
                                return <li key={s.id}>
                                    <button type="button" onClick={() => setDetails(s)} data-testid={`service-card-${s.id}`} className={`w-full text-left rounded-2xl border p-5 ${active ? "border-gold bg-gold/15" : blocked ? "border-white/15 bg-white/5 text-white/60" : "hairline bg-navy-500/25 hover:border-gold/50"}`}>
                                        <span className="flex items-start justify-between gap-3"><span className="font-serif text-xl">{r.option_label || s.name}</span>{active ? <Check className="text-gold shrink-0"/> : blocked ? <LockKeyhole size={20} className="shrink-0"/> : null}</span>
                                        <span className="block text-sm mt-2">{type === "addon" ? "Add-on area" : "Main treatment"} · {formatDuration(s.duration)} · {formatPrice(s.price)}</span>
                                        <span className="block text-sm mt-3 underline">View details</span>
                                        <OfferPrice service={s}/>
                                    </button>

                                </li>;
                            })}
                        </React.Fragment>)}</ul>
                    </section>;
                })}
            </div>}
            <TreatmentDetails service={details} rule={rules[String(details?.id)]} blocked={details ? treatmentBlock(details, rules, cart) : ''} selected={details && (multi ? cart.some(c => c.service.id === details.id) : selected?.id === details.id)} onClose={() => setDetails(null)} onConfirm={() => {select(details); setDetails(null); setReviewing(multi); window.scrollTo?.(0, 0);}}/>
        </div>
    );
}
